import { setTweakValue, useTweakValue } from '@/lib/tweak-store';
import { type EnumDef, TWEAK_DEFS, type UserRole } from '@mss_creator/shared';
import { Alert, Badge, Button, Select } from '@pms-digital/design-system';
import { SettingRow } from './setting-row';

const ROLE_DEF = TWEAK_DEFS.find(
  (def): def is EnumDef => def.key === 'user-role-override' && def.type === 'enum',
);

/** "utilisateur client", "PMS admin": the option label as it reads mid-sentence. */
function inSentence(label: string): string {
  return label.startsWith('PMS') ? label : `${label.charAt(0).toLowerCase()}${label.slice(1)}`;
}

interface RoleOverrideRowProps {
  /** `user.role`, never the simulated one: the page ignores the simulation it hosts. */
  realRole: UserRole;
}

/**
 * The "Rôle simulé" setting, drawn as a list rather than tiles (it has no
 * visual effect to preview). It has no "Modifié" / "Par défaut" pair: while a
 * simulation is on, a pill and a "Revenir à mon rôle" button stand in for them.
 */
export function RoleOverrideRow({ realRole }: RoleOverrideRowProps) {
  if (!ROLE_DEF) throw new Error("The 'user-role-override' tweak is missing from the registry.");
  const def = ROLE_DEF;
  const value = String(useTweakValue(def));
  const simulating = value !== 'inherit';
  const labelOf = (roleValue: string): string =>
    def.options.find((option) => option.value === roleValue)?.label ?? roleValue;

  return (
    <SettingRow
      label={def.label}
      description={def.description}
      scope="browser"
      status={
        simulating ? (
          <Badge tone="info" maxWidth="none">
            Simulation en cours
          </Badge>
        ) : undefined
      }
    >
      <div className="max-w-[320px]">
        <Select
          label={<span className="sr-only">{def.label}</span>}
          value={value}
          onValueChange={(next) => setTweakValue(def, next)}
          options={def.options.map((option) => ({
            value: option.value,
            label:
              option.value === 'inherit' ? `${option.label} (${labelOf(realRole)})` : option.label,
          }))}
        />
      </div>
      {simulating && (
        <div className="mt-3 max-w-[620px]">
          <Alert
            tone="info"
            action={
              <Button variant="primary" size="sm" onClick={() => setTweakValue(def, 'inherit')}>
                Revenir à mon rôle
              </Button>
            }
          >
            <span>
              Vous voyez l'app comme un <b className="text-noir">{inSentence(labelOf(value))}</b>.
              Votre vrai rôle : {labelOf(realRole)}.
            </span>
          </Alert>
        </div>
      )}
    </SettingRow>
  );
}
